import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import type {
  Checklist,
  ChecklistCriteria,
  CriteriaInstance,
  Section,
} from "~/types/Checklist";
import { AssignmentsService } from "~/classes/models/assignments/assignments.service";

type ReferenceChecklistProgress = Record<
  string,
  {
    complete?: boolean;
    positions?: Record<string, boolean>;
  }
>;

export const useAssignmentReferenceChecklist = (documentId: string) =>
  defineStore(`/documents/${documentId}/assignment-reference-checklist`, () => {
    const checklist = ref<Checklist>();
    const criteriaDefinitions = ref<Record<string, ChecklistCriteria>>({});
    const excusedCriteria = ref<string[]>([]);
    const progress = ref<ReferenceChecklistProgress>({});
    const storageKey = ref<string>();
    const isLoading = ref(false);
    const isInitialized = ref(false);
    const error = ref<string>();
    // Shared between the writing panel and the submission review panel.
    // This is a UI step only; continuing never submits the paper.
    const hasContinuedToReview = ref(false);

    const db = useFirestore();

    const fetchSections = async (
      path: string,
      inheritedParagraphPositions = undefined as
        | Checklist["paragraphPositionsData"]
        | undefined
    ): Promise<Section[]> => {
      const sectionsSnapshot = await getDocs(
        query(collection(db, path), orderBy("displayOrder", "asc"))
      );

      const sections: Section[] = [];

      for (const sectionSnapshot of sectionsSnapshot.docs) {
        const section = {
          ...(sectionSnapshot.data() as Section),
          id: sectionSnapshot.id,
        };
        const paragraphPositions =
          section.paragraphPositionsData ??
          sectionSnapshot.data().paragraphPositionsLabels ??
          inheritedParagraphPositions;

        section.criteriaInstances = (section.criteriaInstances ?? []).map(
          (instance) => ({
            ...instance,
            checklistSectionId: section.id,
            paragraphPositionsData:
              instance.paragraphPositionsData ??
              (instance as CriteriaInstance & { paragraphPositionsLabels?: Checklist["paragraphPositionsData"] }).paragraphPositionsLabels ??
              paragraphPositions,
          })
        );
        section.sections = await fetchSections(
          `${path}/${section.id}/sections`,
          paragraphPositions
        );
        sections.push(section);
      }

      return sections;
    };

    const loadProgress = () => {
      progress.value = {};
      if (!import.meta.client || !storageKey.value) return;

      try {
        const savedProgress = localStorage.getItem(storageKey.value);
        if (savedProgress) {
          const parsed = JSON.parse(savedProgress);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            progress.value = Object.fromEntries(
              Object.entries(parsed).filter(([, value]) =>
                value != null && typeof value === "object" && !Array.isArray(value)
              )
            ) as ReferenceChecklistProgress;
          }
        }
      } catch {
        progress.value = {};
      }
    };

    const saveProgress = () => {
      if (!import.meta.client || !storageKey.value) return;
      try {
        localStorage.setItem(storageKey.value, JSON.stringify(progress.value));
      } catch {
        // Keep the current session usable when browser storage is unavailable.
      }
    };

    const initialize = async () => {
      if (isInitialized.value || isLoading.value) return;

      const submittedDocument =
        useSubmittedDocumentStore(documentId).submittedDocument;

      if (
        !submittedDocument?.assignmentId ||
        !isAssignmentReferenceChecklistDraft(submittedDocument)
      ) {
        isInitialized.value = true;
        return;
      }

      isLoading.value = true;
      error.value = undefined;

      try {
        const assignment = await AssignmentsService.get(
          submittedDocument.assignmentId
        );

        const checklistSelection = resolveAssignmentReferenceChecklist(
          assignment,
          submittedDocument.studentId
        );

        if (!checklistSelection) {
          isInitialized.value = true;
          return;
        }

        const { checklistId, checklistPath } = checklistSelection;

        const normalizedPath = checklistPath.replace(/\/$/, "");
        const checklistSnapshot = await getDoc(
          doc(db, `${normalizedPath}/${checklistId}`)
        );

        if (!checklistSnapshot.exists()) {
          throw new Error("The assignment checklist could not be found.");
        }

        const loadedChecklist = {
          ...(checklistSnapshot.data() as Checklist),
          id: checklistSnapshot.id,
          path: normalizedPath,
        };
        loadedChecklist.paragraphPositionsData ??=
          checklistSnapshot.data().paragraphPositionsLabels;
        for (const instance of [
          ...(loadedChecklist.criteriaInstances ?? []),
          ...(loadedChecklist.customCriteriaInstances ?? []),
        ]) {
          instance.paragraphPositionsData ??= loadedChecklist.paragraphPositionsData;
        }
        loadedChecklist.sections = await fetchSections(
          `${normalizedPath}/${checklistId}/sections`,
          loadedChecklist.paragraphPositionsData
        );

        const criteriaSnapshot = await getDocs(
          collection(db, "checklistCriteria")
        );
        criteriaDefinitions.value = Object.fromEntries(
          criteriaSnapshot.docs.map((criteriaSnapshot) => [
            criteriaSnapshot.id,
            {
              ...(criteriaSnapshot.data() as ChecklistCriteria),
              id: criteriaSnapshot.id,
            },
          ])
        );

        const documentClassroomData =
          useDocumentClassroomDataStore(documentId);
        excusedCriteria.value =
          documentClassroomData.student?.excusedCriteria ?? [];
        checklist.value = loadedChecklist;
        storageKey.value = [
          "scribekick",
          "reference-checklist",
          "v1",
          documentId,
          normalizedPath,
          checklistId,
        ].join(":");
        loadProgress();
      } catch (initializationError) {
        error.value =
          initializationError instanceof Error
            ? initializationError.message
            : "The assignment checklist could not be loaded.";
      } finally {
        isLoading.value = false;
        isInitialized.value = !error.value;
      }
    };

    const criterionDefinition = (instance: CriteriaInstance) => {
      if (!instance.checklistCriteriaId) return undefined;
      return criteriaDefinitions.value[instance.checklistCriteriaId];
    };

    const criterionKey = (
      sectionPath: string,
      instance: CriteriaInstance,
      index: number
    ) =>
      [
        sectionPath,
        instance.checklistCriteriaId ?? "custom",
        instance.displayOrder ?? index,
        index,
      ].join(":");

    const isComplete = (key: string, position?: number) => {
      if (position == undefined) return progress.value[key]?.complete ?? false;
      return progress.value[key]?.positions?.[position] ?? false;
    };

    const toggleComplete = (key: string, position?: number) => {
      const existing = progress.value[key] ?? {};

      if (position == undefined) {
        existing.complete = !existing.complete;
      } else {
        existing.positions = existing.positions ?? {};
        existing.positions[position] = !existing.positions[position];
      }

      progress.value = { ...progress.value, [key]: existing };
      saveProgress();
    };

    const clearProgress = () => {
      if (import.meta.client && storageKey.value) {
        try {
          localStorage.removeItem(storageKey.value);
        } catch {
          // Clearing the in-memory progress should still succeed.
        }
      }
      progress.value = {};
    };

    return {
      checklist,
      excusedCriteria,
      isLoading,
      isInitialized,
      error,
      hasContinuedToReview,
      initialize,
      criterionDefinition,
      criterionKey,
      isComplete,
      toggleComplete,
      clearProgress,
    };
  });
